import React from 'react'
import { useSelector } from 'react-redux'
import { TextField, Typography } from '@material-ui/core'
import { AccessTime as AccessTimeIcon, Info as InfoIcon } from '@material-ui/icons'
import { KeyboardDatePicker, KeyboardTimePicker } from '@material-ui/pickers'
import clsx from 'clsx'
import { useFormikContext } from 'formik'
import moment from 'moment'
import { CustomizedTooltip, PriceInput } from 'shared'
import { DATE_FORMAT, STRING_EMPTY, TIME_FORMAT } from 'utils/constants'

import { useStyles } from './InstallmentOption.style'

const InstallmentOption = ({ installment, installmentsCount, index, readOnly }) => {
  const classes = useStyles()

  const { setValues, handleBlur, touched, errors } = useFormikContext()
  const { event } = useSelector((state) => state.events)

  const handleChangePrice = (setValues) => (e) => {
    setValues((prevValues) => {
      const newInstallments = [...prevValues.installments]
      newInstallments[index] = {
        ...newInstallments[index],
        amount: e.target.value
      }
      return {
        ...prevValues,
        installments: newInstallments
      }
    })
  }

  const handleChangeDate = (setValues, field, format, timeField, defaultTime) => (date) =>
    setValues((values) => {
      const newInstallments = [...values.installments]
      newInstallments[index] = {
        ...newInstallments[index],
        [field]: moment(date).isValid() ? moment(date).format(format) : date,
        ...(timeField && {
          [timeField]:
            !newInstallments[index][timeField] && !!date && moment(date).isValid()
              ? defaultTime
              : newInstallments[index][timeField]
        })
      }

      return {
        ...values,
        installments: newInstallments
      }
    })

  return (
    <div className={classes.installmentContainer}>
      <Typography color='primary' variant='h6'>
        Cuota N° {installment.number}
      </Typography>

      <div className={classes.installmentOption}>
        <div className={classes.formGroup}>
          <Typography className={classes.label} variant='h6' color='primary'>
            Precio*
          </Typography>

          <TextField
            value={installment.amount}
            placeholder='Ej: $2000'
            variant='outlined'
            name='price'
            disabled={readOnly}
            onChange={handleChangePrice(setValues)}
            onBlur={handleBlur}
            InputProps={{
              inputComponent: PriceInput
            }}
            error={
              touched.installments &&
              touched.installments[index] &&
              touched.installments[index].amount &&
              errors.installments &&
              errors.installments[index] &&
              Boolean(errors.installments[index].amount)
            }
            helperText={
              errors.installments && errors.installments[index] && errors.installments[index].amount
            }
            className={classes.textField}
          />
        </div>
        <div className={classes.formGroup}>
          <Typography className={classes.label} variant='h6' color='primary'>
            Inicio de venta*
          </Typography>
          <div className={classes.datePickerContainer}>
            <KeyboardDatePicker
              className={clsx(classes.inputDateField, classes.dateField)}
              autoOk
              name='startDate'
              format='DD/MM/YYYY'
              variant='inline'
              disabled={readOnly}
              margin='none'
              id='date-picker-inline'
              onBlur={handleBlur}
              inputVariant='outlined'
              value={installment.startDate}
              KeyboardButtonProps={{
                'aria-label': 'change date'
              }}
              minDateMessage={STRING_EMPTY}
              maxDateMessage={STRING_EMPTY}
              invalidDateMessage={STRING_EMPTY}
              placeholder='dd/mm/aaaa'
              error={
                touched.installments &&
                touched.installments[index] &&
                touched.installments[index].startDate &&
                errors.installments &&
                errors.installments[index] &&
                Boolean(errors.installments[index].startDate)
              }
              helperText={
                errors.installments &&
                errors.installments[index] &&
                errors.installments[index].startDate
              }
              onChange={handleChangeDate(setValues, 'startDate', 'YYYY-MM-DD')}
            />
          </div>
        </div>
        <div className={classes.saleToContainer}>
          <Typography color='primary' variant='h6' className={classes.label}>
            Fin de la venta*
          </Typography>
          <div className={classes.saleToContainer}>
            <KeyboardDatePicker
              className={clsx(classes.inputDateField, classes.dateField)}
              autoOk
              name='endDate'
              format='DD/MM/YYYY'
              disabled={readOnly}
              variant='inline'
              margin='none'
              id='date-picker-inline'
              inputVariant='outlined'
              value={installment.endDate}
              KeyboardButtonProps={{
                'aria-label': 'change date'
              }}
              minDateMessage={STRING_EMPTY}
              maxDateMessage={STRING_EMPTY}
              invalidDateMessage={STRING_EMPTY}
              onBlur={handleBlur}
              placeholder='dd/mm/aaaa'
              error={
                touched.installments &&
                touched.installments[index] &&
                touched.installments[index].endDate &&
                Boolean(
                  errors.installments &&
                    errors.installments[index] &&
                    errors.installments[index].endDate
                )
              }
              onChange={handleChangeDate(
                setValues,
                'endDate',
                'YYYY-MM-DD',
                'endDateTime',
                moment('23:59', 'HH:mm').format()
              )}
            />
            {installment.number === installmentsCount && (
              <>
                <KeyboardTimePicker
                  className={clsx(`${classes.inputDateField} timePicker`, classes.dateField)}
                  autoOk
                  name='endDateTime'
                  variant='inline'
                  onBlur={handleBlur}
                  inputVariant='outlined'
                  value={installment.endDateTime}
                  keyboardIcon={<AccessTimeIcon />}
                  placeholder='hh:mm am'
                  disabled={readOnly}
                  invalidDateMessage={STRING_EMPTY}
                  minDateMessage={STRING_EMPTY}
                  maxDateMessage={STRING_EMPTY}
                  error={touched.endDateTime && Boolean(errors.endDateTime)}
                  onChange={handleChangeDate(setValues, 'endDateTime')}
                />

                {moment(
                  `${moment(installment.endDate).format(DATE_FORMAT)} ${moment(
                    installment.endDateTime
                  ).format(TIME_FORMAT)}`
                ).isAfter(event.from) && (
                  <CustomizedTooltip
                    title='Sugerencia: configurar la fecha/hora de fin de venta antes de inicio del evento'
                    className={classes.switchTooltip}
                    isTopMost
                    arrowClassName={classes.switchTooltipArrow}
                    disabledClickAway
                    buttonClassName={classes.tooltipButton}
                    position='right-end'>
                    {({ handleTooltip }) => (
                      <InfoIcon className={classes.errorIcon} onClick={handleTooltip} />
                    )}
                  </CustomizedTooltip>
                )}
              </>
            )}
          </div>
          {errors.installments && errors.installments[index] && errors.installments[index].endDate && (
            <Typography variant='caption' color='error'>
              {errors.installments[index].endDate}
            </Typography>
          )}
        </div>
      </div>
    </div>
  )
}

export default InstallmentOption
